import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../api";
import { plausibleEvent } from "../utils/track";

const AuthContext = createContext();

// Matches the server (IDLE_LIMITS): an admin panel left open is the real risk, a shopper
// browsing deals should never be asked to sign in again.
const IDLE_MS = { admin: 2 * 60 * 60 * 1000, business: 24 * 60 * 60 * 1000 };

// A shared phone is normal here. The service worker keeps a copy of your session and your saved
// codes for when signal drops, so both must go the moment anybody signs in or out.
const purgePrivateCache = () => {
  try { navigator.serviceWorker?.controller?.postMessage("PURGE_PRIVATE"); } catch { /* no worker */ }
};

const saveToken = (token) => {
  purgePrivateCache();
  localStorage.setItem("lp-token", token);
};

const clearToken = () => {
  localStorage.removeItem("lp-token");
  purgePrivateCache();
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const limit = IDLE_MS[user?.role];
    if (!limit) return;
    const touch = () => localStorage.setItem("lp-active", String(Date.now()));
    touch();
    const events = ["pointerdown", "keydown", "visibilitychange"];
    events.forEach((e) => window.addEventListener(e, touch, { passive: true }));
    const timer = setInterval(() => {
      const last = Number(localStorage.getItem("lp-active") || 0);
      if (last && Date.now() - last > limit) {
        clearToken();
        window.location.assign("/login?ended=idle");
      }
    }, 60000);
    return () => {
      clearInterval(timer);
      events.forEach((e) => window.removeEventListener(e, touch));
    };
  }, [user?.role]);

  useEffect(() => {
    const token = localStorage.getItem("lp-token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => clearToken())
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password, portal) => {
    const deviceToken = localStorage.getItem("lp-device");
    const res = await api.post("/auth/login", { email, password, ...(deviceToken ? { deviceToken } : {}), ...(portal ? { portal } : {}) });
    if (res.data.requiresMfa) {
      return { requiresMfa: true, challengeToken: res.data.challengeToken };
    }
    saveToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const loginWithPasskey = async (email, portal) => {
    const { startAuthentication } = await import("@simplewebauthn/browser");
    const { data: start } = await api.post("/auth/passkey/options", email ? { email } : {});
    const credential = await startAuthentication({ optionsJSON: start.options });
    const res = await api.post("/auth/passkey/finish", { state: start.state, credential, ...(portal ? { portal } : {}) });
    saveToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const completeMfa = async (challengeToken, code, trustDevice = false) => {
    const deviceLabel = (navigator.userAgent || "This device").slice(0, 80);
    const res = await api.post("/auth/mfa/login", { challengeToken, code, trustDevice, deviceLabel });
    saveToken(res.data.token);
    if (res.data.deviceToken) localStorage.setItem("lp-device", res.data.deviceToken);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (payload) => {
    const res = await api.post("/auth/register", payload);
    saveToken(res.data.token);
    setUser(res.data.user);
    plausibleEvent("Signup", { role: "business" });
    return res.data.user;
  };

  const registerShopper = async (payload) => {
    const res = await api.post("/auth/register/shopper", payload);
    saveToken(res.data.token);
    setUser(res.data.user);
    plausibleEvent("Signup", { role: "shopper" });
    return res.data.user;
  };

  const refresh = async () => {
    try { const res = await api.get("/auth/me"); setUser(res.data); return res.data; }
    catch { return null; }
  };

  const logout = () => {
    clearToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithPasskey, completeMfa, register, registerShopper, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
